import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { queryKeys } from '@/lib/queryKeys';

// Type pour les heures supplémentaires liées à une affectation
export interface HeuresSupAffectation {
  total_heures: number;
  total_cout: number;
  count: number;
}

// Type pour une affectation historique
export interface AffectationHistorique {
  id: string;
  chantier_id: string;
  membre_equipe_id: string;
  equipe_id: string | null;
  date_debut: string;
  date_fin: string;
  jours_travailles: number;
  heures_par_jour: number | null;
  heures_dernier_jour: number | null;
  taux_horaire_specifique: number | null;
  charges_patronales_pct: number | null;
  notes: string | null;
  created_at: string;
  // Relation chantier
  chantier: {
    id: string;
    nom_chantier: string;
    couleur: string | null;
    pictogramme: string | null;
  };
  // Calculés côté client
  cout_mo?: number;
  total_heures?: number; // Total heures travaillées (jours × heures/jour)
  heures_sup?: HeuresSupAffectation; // Heures supplémentaires liées
}

// Type pour le groupement par année
export interface AffectationsParAnnee {
  annee: number;
  affectations: AffectationHistorique[];
  totalJours: number;
  totalCout: number;
}

interface UseMemberHistoryParams {
  membreId: string | null;
  tauxHoraire: number; // Taux horaire actuel du membre (fallback)
  chargesPatronales?: number; // DEPRECATED - utiliser coefficientCharges
  coefficientCharges?: number; // ex: 1.85 (fallback)
}

interface UseMemberHistoryReturn {
  // Données
  affectations: AffectationHistorique[];
  affectationsParAnnee: AffectationsParAnnee[];
  isLoading: boolean;
  error: string | null;

  // Statistiques globales
  totalJoursOuvres: number;
  totalCoutMO: number;
  nombreChantiers: number;

  // Pagination "Voir plus"
  visibleCount: number;
  hasMore: boolean;
  showMore: () => void;

  // Pagination par page (après 50)
  currentPage: number;
  totalPages: number;
  goToPage: (page: number) => void;

  // Rafraîchissement
  refresh: () => void;
}

const INITIAL_COUNT = 10; // Afficher 10 au départ
const PAGE_SIZE = 50; // Pagination par 50 ensuite

// Données brutes retournées par la query
interface MemberHistoryRawData {
  totalCount: number;
  rawAffectations: any[];
  heuresSupData: any[];
}

/**
 * Hook pour charger l'historique des affectations d'un membre
 * Pagination : 10 initiales → "Voir plus" (+40 = 50) → flèches pour les 50 suivantes
 *
 * Migré vers React Query (useQuery).
 */
export function useMemberHistory({
  membreId,
  tauxHoraire,
  coefficientCharges,
}: UseMemberHistoryParams): UseMemberHistoryReturn {
  const { entrepriseId } = useSubscription();

  // État de pagination (UI locale, pas des données serveur)
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);
  const [currentPage, setCurrentPage] = useState(0); // 0 = première page (10 + 40)

  // Reset pagination quand le membre change
  useEffect(() => {
    setVisibleCount(INITIAL_COUNT);
    setCurrentPage(0);
  }, [membreId]);

  // -------------------------------------------------------------------------
  // Query : count + affectations paginées + heures_sup
  // -------------------------------------------------------------------------
  const {
    data,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: [...queryKeys.equipe.history(entrepriseId ?? '', membreId ?? ''), currentPage],
    queryFn: async (): Promise<MemberHistoryRawData> => {
      if (import.meta.env.DEV) console.log('[useMemberHistory] Fetching page', currentPage, 'for membre', membreId);

      // 1. Compter le total
      const { count, error: countError } = await supabase
        .from('affectations_chantiers')
        .select('*', { count: 'exact', head: true })
        .eq('membre_equipe_id', membreId!);

      if (countError) throw countError;

      // 2. Calculer offset et limite selon la page
      let offset = 0;
      const limit = PAGE_SIZE;

      if (currentPage === 0) {
        offset = 0;
      } else {
        offset = PAGE_SIZE + (currentPage - 1) * PAGE_SIZE;
      }

      // 3. Fetch les affectations avec relation chantier
      const { data: affData, error: fetchError } = await supabase
        .from('affectations_chantiers')
        .select(`
          id, chantier_id, membre_equipe_id, equipe_id,
          date_debut, date_fin, jours_travailles, heures_par_jour,
          heures_dernier_jour, taux_horaire_specifique, charges_patronales_pct,
          notes, created_at,
          chantier:chantiers(id, nom_chantier, couleur, pictogramme)
        `)
        .eq('membre_equipe_id', membreId!)
        .order('date_debut', { ascending: false })
        .range(offset, offset + limit - 1);

      if (fetchError) throw fetchError;

      // 4. Fetch les heures supplémentaires du membre
      const { data: hsData, error: hsError } = await supabase
        .from('heures_supplementaires')
        .select('chantier_id, date_travail, nombre_heures, cout_total')
        .eq('membre_id', membreId!);

      if (hsError) {
        console.warn('[useMemberHistory] Erreur heures sup:', hsError);
        // On continue sans les heures sup
      }

      return {
        totalCount: count ?? 0,
        rawAffectations: affData || [],
        heuresSupData: hsData || [],
      };
    },
    enabled: !!membreId && !!entrepriseId,
    staleTime: 3 * 60 * 1000, // 3 minutes
  });

  // -------------------------------------------------------------------------
  // Transformation : calculer coûts + joindre heures_sup
  // -------------------------------------------------------------------------
  const affectations = useMemo((): AffectationHistorique[] => {
    if (!data) return [];

    // Grouper les heures sup par chantier_id pour un accès rapide
    const heuresSupParChantier = new Map<string, { date: string; heures: number; cout: number }[]>();
    (data.heuresSupData || []).forEach((hs: any) => {
      if (!heuresSupParChantier.has(hs.chantier_id)) {
        heuresSupParChantier.set(hs.chantier_id, []);
      }
      heuresSupParChantier.get(hs.chantier_id)!.push({
        date: hs.date_travail,
        heures: hs.nombre_heures,
        cout: hs.cout_total,
      });
    });

    // Transformer les données et calculer les coûts
    return (data.rawAffectations || []).map((aff: any) => {
      const affectation: AffectationHistorique = {
        ...aff,
        chantier: aff.chantier || { id: '', nom_chantier: 'Chantier inconnu', couleur: null, pictogramme: null },
        cout_mo: 0,
        total_heures: 0,
        heures_sup: undefined,
      };

      // Calculer coût MO
      const taux = affectation.taux_horaire_specifique ?? tauxHoraire;
      const coeffCharges = coefficientCharges || 1.85;
      const heuresParJour = affectation.heures_par_jour ?? 8;

      if (affectation.heures_dernier_jour !== null && affectation.heures_dernier_jour !== undefined) {
        const joursComplets = Math.max(0, affectation.jours_travailles - 1);
        const coutJoursComplets = joursComplets * heuresParJour * taux * coeffCharges;
        const coutDernierJour = affectation.heures_dernier_jour * taux * coeffCharges;
        affectation.cout_mo = coutJoursComplets + coutDernierJour;
      } else {
        affectation.cout_mo = affectation.jours_travailles * heuresParJour * taux * coeffCharges;
      }

      // Calculer total heures
      if (affectation.heures_dernier_jour !== null && affectation.heures_dernier_jour !== undefined) {
        const joursComplets = Math.max(0, affectation.jours_travailles - 1);
        affectation.total_heures = joursComplets * heuresParJour + affectation.heures_dernier_jour;
      } else {
        affectation.total_heures = affectation.jours_travailles * heuresParJour;
      }

      // Chercher les heures sup liées à cette affectation (même chantier, date dans la période)
      const hsChantier = heuresSupParChantier.get(aff.chantier_id);
      if (hsChantier && hsChantier.length > 0) {
        const dateDebut = new Date(aff.date_debut);
        const dateFin = new Date(aff.date_fin);

        const hsLiees = hsChantier.filter((hs) => {
          const dateHS = new Date(hs.date);
          return dateHS >= dateDebut && dateHS <= dateFin;
        });

        if (hsLiees.length > 0) {
          affectation.heures_sup = {
            total_heures: hsLiees.reduce((sum, hs) => sum + hs.heures, 0),
            total_cout: hsLiees.reduce((sum, hs) => sum + hs.cout, 0),
            count: hsLiees.length,
          };
        }
      }

      return affectation;
    }) as AffectationHistorique[];
  }, [data, tauxHoraire, coefficientCharges]);

  // -------------------------------------------------------------------------
  // Groupement par année (limité aux éléments visibles)
  // -------------------------------------------------------------------------
  const affectationsParAnnee = useMemo((): AffectationsParAnnee[] => {
    const grouped = new Map<number, AffectationHistorique[]>();
    const visibleAffectations = affectations.slice(0, visibleCount);

    visibleAffectations.forEach((aff) => {
      const annee = new Date(aff.date_debut).getFullYear();
      if (!grouped.has(annee)) {
        grouped.set(annee, []);
      }
      grouped.get(annee)!.push(aff);
    });

    // Convertir en tableau trié par année décroissante
    return Array.from(grouped.entries())
      .sort(([a], [b]) => b - a)
      .map(([annee, affs]) => ({
        annee,
        affectations: affs,
        totalJours: affs.reduce((sum, a) => sum + a.jours_travailles, 0),
        totalCout: affs.reduce((sum, a) => sum + (a.cout_mo ?? 0), 0),
      }));
  }, [affectations, visibleCount]);

  // -------------------------------------------------------------------------
  // Statistiques globales (sur les données visibles)
  // -------------------------------------------------------------------------
  const totalJoursOuvres = useMemo(() => {
    return affectations.slice(0, visibleCount).reduce((sum, a) => sum + a.jours_travailles, 0);
  }, [affectations, visibleCount]);

  const totalCoutMO = useMemo(() => {
    return affectations.slice(0, visibleCount).reduce((sum, a) => sum + (a.cout_mo ?? 0), 0);
  }, [affectations, visibleCount]);

  const nombreChantiers = useMemo(() => {
    const uniqueChantiers = new Set(affectations.slice(0, visibleCount).map((a) => a.chantier_id));
    return uniqueChantiers.size;
  }, [affectations, visibleCount]);

  // -------------------------------------------------------------------------
  // Actions de pagination
  // -------------------------------------------------------------------------
  const showMore = useCallback(() => {
    // Passer de 10 à 50
    setVisibleCount(PAGE_SIZE);
  }, []);

  const goToPage = useCallback((page: number) => {
    setCurrentPage(page);
    setVisibleCount(PAGE_SIZE); // Toujours afficher 50 par page
  }, []);

  // Calcul si "voir plus" doit s'afficher
  const hasMore = visibleCount < PAGE_SIZE && affectations.length > INITIAL_COUNT;

  // Nombre de pages total
  const totalPages = Math.ceil((data?.totalCount ?? 0) / PAGE_SIZE);

  return {
    affectations: affectations.slice(0, visibleCount),
    affectationsParAnnee,
    isLoading,
    error: queryError?.message ?? null,
    totalJoursOuvres,
    totalCoutMO,
    nombreChantiers,
    visibleCount,
    hasMore,
    showMore,
    currentPage,
    totalPages,
    goToPage,
    refresh: () => { refetch(); },
  };
}
